import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TEL_HREF } from "@/lib/ascentix";
import Wordmark from "@/components/Wordmark";

const links = [
  { id: "about", label: "About" },
  { id: "programmes", label: "Programmes" },
  { id: "approach", label: "Approach" },
  { id: "clients", label: "Clients" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#fdfbf7]/80 border-b border-slate-900/5 shadow-[0_2px_20px_-10px_rgba(15,23,42,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <Wordmark size="md" />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleClick(l.id)}
              className="text-[15px] text-slate-700 hover:text-orange-600 transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-orange-600 hover:after:w-full after:transition-[width] after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            data-testid="nav-cta-book"
            onClick={() => handleClick("contact")}
            className="hidden sm:inline-flex rounded-full bg-orange-600 hover:bg-orange-700 text-white font-medium px-5 h-10 shadow-[0_8px_20px_-8px_rgba(234,88,12,0.6)]"
          >
            Book a Consultation
          </Button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-900/10 bg-white text-slate-900"
            aria-label="Toggle menu"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 backdrop-blur-xl bg-[#fdfbf7]/95 border-b border-slate-900/5 ${
          open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-5 flex flex-col gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => handleClick(l.id)}
              className="text-left py-3 text-[17px] font-medium text-slate-800 border-b border-slate-900/5"
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="nav-mobile-cta-book"
            onClick={() => handleClick("contact")}
            className="mt-4 rounded-full bg-orange-600 hover:bg-orange-700 text-white h-12"
          >
            Book a Consultation
          </Button>
        </div>
      </div>
    </header>
  );
}
